import { useState } from 'react';
import { Check, ChevronDown, ChevronsUpDown, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { cmdkNormalizedFilter } from '@/lib/utils/searchUtils'; // A-quattuor.6
import { usePaymentConditions } from '@/hooks/usePaymentConditions';
import type { PaymentCondition } from '@/types/paymentCondition';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface PaymentConditionGroupedSelectorProps {
  value: string;
  onChange: (value: string) => void;
  totalValue: number;
  disabled?: boolean;
  error?: string;
  /** L-FAT.1 D-FAT.17: condições com config.month_end só aparecem para cliente de
   *  fechamento mensal. Default false → filtradas antes do agrupamento. */
  allowMonthEnd?: boolean;
}

interface GroupedConditions {
  cash: PaymentCondition[];
  installments: PaymentCondition[];
  byDueDate: PaymentCondition[];
  /** L-FAT.1 D-FAT.5: config.month_end — grupo próprio, sem dias */
  monthEnd: PaymentCondition[];
  other: PaymentCondition[];
}

export function PaymentConditionGroupedSelector({
  value,
  onChange,
  totalValue,
  disabled = false,
  error,
  allowMonthEnd = false,
}: PaymentConditionGroupedSelectorProps) {
  const [open, setOpen] = useState(false);
  const [installmentDialogOpen, setInstallmentDialogOpen] = useState(false);
  const { conditions: allConditions, isLoading, filterConditionsByValue } = usePaymentConditions();

  // L-FAT.1 D-FAT.17: fora do cliente de fechamento, month_end sai ANTES do agrupamento
  const conditions = allowMonthEnd
    ? allConditions
    : allConditions.filter(c => c.config.month_end !== true);

  // Filter conditions by value
  const availableConditions = filterConditionsByValue(conditions, totalValue);
  const blockedConditions = conditions.filter(
    c => !availableConditions.find(ac => ac.id === c.id)
  );

  // Group conditions
  const groupedConditions: GroupedConditions = conditions.reduce(
    (acc, condition) => {
      if (condition.config.month_end === true) {
        acc.monthEnd.push(condition);
        return acc;
      }
      switch (condition.condition_type) {
        case 'cash':
          acc.cash.push(condition);
          break;
        case 'by_installments':
          acc.installments.push(condition);
          break;
        case 'by_due_date':
          acc.byDueDate.push(condition);
          break;
        default:
          acc.other.push(condition);
      }
      return acc;
    },
    { cash: [], installments: [], byDueDate: [], monthEnd: [], other: [] } as GroupedConditions
  );

  // Sort installments numerically
  groupedConditions.installments.sort((a, b) => {
    const aNum = a.config.installments || 0;
    const bNum = b.config.installments || 0;
    return aNum - bNum;
  });

  // Sort by due date
  groupedConditions.byDueDate.sort((a, b) => {
    const aDays = Array.isArray(a.config.days) ? a.config.days[0] : (a.config.days || 0);
    const bDays = Array.isArray(b.config.days) ? b.config.days[0] : (b.config.days || 0);
    return aDays - bDays;
  });

  const selectedCondition = conditions.find(
    c => c.id.toLowerCase() === value?.toLowerCase()
  );

  const isConditionAvailable = (conditionId: string) => {
    return availableConditions.some(
      c => c.id.toLowerCase() === conditionId?.toLowerCase()
    );
  };

  const getBlockedReason = (condition: PaymentCondition): string | null => {
    const minValue = condition.config.min_sale_value;
    if (minValue && totalValue < minValue) {
      return `Mín: R$ ${minValue.toFixed(2)}`;
    }
    return null;
  };

  const renderConditionItem = (condition: PaymentCondition) => {
    const isAvailable = isConditionAvailable(condition.id);
    const blockedReason = getBlockedReason(condition);
    const isSelected = value?.toLowerCase() === condition.id.toLowerCase();

    return (
      <CommandItem
        key={condition.id}
        // A-quattuor.6: value = NOME (o cmdk filtra pelo value; com UUID a busca por nome nunca casava);
        // o UUID segue no onSelect
        value={condition.name}
        onSelect={() => {
          if (isAvailable) {
            onChange(condition.id);
            setTimeout(() => setOpen(false), 50); // Delay para evitar race condition
          }
        }}
        disabled={!isAvailable}
        className={cn(
          'flex items-center justify-between',
          !isAvailable && 'opacity-50 cursor-not-allowed'
        )}
      >
        <div className="flex items-center gap-2">
          <Check
            className={cn(
              'h-4 w-4',
              isSelected ? 'opacity-100' : 'opacity-0'
            )}
          />
          <span>{condition.name}</span>
        </div>
        {!isAvailable && blockedReason && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Info className="h-3 w-3" />
                  <span>{blockedReason}</span>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>Valor mínimo da venda não atingido</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </CommandItem>
    );
  };

  const handleInstallmentSelect = (condition: PaymentCondition) => {
    if (isConditionAvailable(condition.id)) {
      onChange(condition.id);
      setInstallmentDialogOpen(false);
      setOpen(false);
    }
  };

  return (
    <div className="space-y-1">
      <Popover open={open} onOpenChange={setOpen} modal>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled || isLoading}
            className={cn(
              'w-full justify-between',
              error && 'border-destructive'
            )}
          >
            {selectedCondition ? selectedCondition.name : 'Selecione a condição...'}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
      <PopoverContent 
        className="w-full p-0 z-50" 
        align="start"
        onOpenAutoFocus={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.stopPropagation()}
      >
        <div 
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          {/* A-quattuor.6: filtro sem acento sobre o nome da condição */}
          <Command filter={cmdkNormalizedFilter}>
            <CommandInput placeholder="Buscar condição..." />
            <CommandList>
              <CommandEmpty>Nenhuma condição encontrada.</CommandEmpty>

              {/* À Vista */}
              {groupedConditions.cash.length > 0 && (
                <CommandGroup heading="À Vista">
                  {groupedConditions.cash.map(renderConditionItem)}
                </CommandGroup>
              )}

              {/* Parcelado */}
              {groupedConditions.installments.length > 0 && (
                <CommandGroup heading="Parcelado">
                  <CommandItem
                    value="__installment_selector__"
                    onSelect={() => {
                      setTimeout(() => {
                        setInstallmentDialogOpen(true);
                        setOpen(false);
                      }, 50); // Delay para evitar race condition
                    }}
                    className="flex items-center justify-between cursor-pointer"
                  >
                    <span>Selecionar parcelamento...</span>
                    <ChevronDown className="h-4 w-4" />
                  </CommandItem>
                </CommandGroup>
              )}

              {/* Prazo (dias) */}
              {groupedConditions.byDueDate.length > 0 && (
                <CommandGroup heading="Prazo">
                  {groupedConditions.byDueDate.map(renderConditionItem)}
                </CommandGroup>
              )}

              {/* Fechamento mensal (L-FAT.1 D-FAT.5) */}
              {groupedConditions.monthEnd.length > 0 && (
                <CommandGroup heading="Fechamento">
                  {groupedConditions.monthEnd.map(renderConditionItem)}
                </CommandGroup>
              )}

              {/* Outros */}
              {groupedConditions.other.length > 0 && (
                <CommandGroup heading="Outros">
                  {groupedConditions.other.map(renderConditionItem)}
                </CommandGroup>
            )}
          </CommandList>
        </Command>
        </div>
      </PopoverContent>
      </Popover>

      {/* Dialog for installment selection */}
      <Dialog 
        open={installmentDialogOpen} 
        onOpenChange={(open) => {
          setInstallmentDialogOpen(open);
          if (open) {
            setOpen(false);
          }
        }}
      >
        <DialogContent 
          className="sm:max-w-md z-[100]" 
          onPointerDownOutside={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <DialogHeader>
            <DialogTitle>Selecione o parcelamento</DialogTitle>
            <DialogDescription>
              Escolha a quantidade de parcelas para esta venda
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-3 gap-2 py-4">
            {groupedConditions.installments.map((condition) => {
              const isAvailable = isConditionAvailable(condition.id);
              const blockedReason = getBlockedReason(condition);
              const isSelected = value?.toLowerCase() === condition.id.toLowerCase();

              return (
                <TooltipProvider key={condition.id}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant={isSelected ? 'default' : 'outline'}
                    onClick={(e) => {
                      handleInstallmentSelect(condition);
                      e.stopPropagation();
                    }}
                    disabled={!isAvailable}
                    className={cn(
                      'h-16',
                      !isAvailable && 'opacity-50 cursor-not-allowed'
                    )}
                  >
                        <div className="flex flex-col items-center gap-1">
                          <span className="text-lg font-bold">
                            {condition.config.installments}x
                          </span>
                          {!isAvailable && blockedReason && (
                            <span className="text-xs">{blockedReason}</span>
                          )}
                        </div>
                      </Button>
                    </TooltipTrigger>
                    {!isAvailable && (
                      <TooltipContent>
                        <p>Valor mínimo da venda não atingido</p>
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      {error && (
        <p className="text-sm text-destructive">{error}</p>
      )}
    </div>
  );
}
